import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, ArrowRight, CheckCircle2, ClipboardList, Clock, Loader2,
  ShieldCheck, XCircle, RefreshCcw,
} from "lucide-react";
import { toast } from "sonner";
import AppNav from "@/components/app/AppNav";
import AssessmentQuestion from "@/components/academy/AssessmentQuestion";
import AssessmentResultPanel from "@/components/academy/AssessmentResultPanel";
import { api, formatApiError } from "@/api/client";

/**
 * Informed OS Academy — Learner assessment take-flow (Phase 1d).
 *
 * Routes:
 *   - /academy/programs/:programSlug/lessons/:lessonSlug/assessment/:assessmentSlug
 *   - /academy/programs/:programSlug/final/:assessmentSlug
 *
 * Flow:
 *   1. GET /academy/programs/:programSlug (for breadcrumb + assessment lookup)
 *   2. Resolve assessment_id by matching slug in the program's assessment set
 *   3. GET /academy/assessments/{id} for metadata + any in-progress/prior state
 *   4. If no in-progress attempt: user clicks "Start attempt" → POST /attempts
 *   5. Renders questions from the start-attempt payload (no answer key)
 *   6. Submit → POST /attempts/{id}/submit → renders result honoring policy
 */
export default function AssessmentTake() {
  const { programSlug, lessonSlug, assessmentSlug } = useParams();
  const navigate = useNavigate();

  const [state, setState] = useState({
    status: "loading",
    program: null,
    assessment: null,
    error: null,
  });
  const [attempt, setAttempt] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});    // { question_id: [option_id, ...] }
  const [submitResult, setSubmitResult] = useState(null);
  const [starting, setStarting] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const loadedOnce = useRef(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setState((s) => ({ ...s, status: "loading", error: null }));
      try {
        const progRes = await api.get(`/academy/programs/${programSlug}`);
        const program = progRes.data;
        // Locate the assessment by slug across all modules + lesson-scoped + program-final.
        // The program payload doesn't currently list assessments; walk lesson→module→program
        // until we find a matching slug by hitting the assessments endpoint directly.
        // Simpler approach for 1d: rely on a direct assessment lookup via slug endpoint
        // isn't shipped, so we fetch the program-full assessment map via the admin-preview
        // API (not available to learners). Instead, we require the caller to supply the
        // assessment_id via query string OR we do a shallow fetch by iterating.
        //
        // For 1d MVP: we require the containing page (LessonPlayer or ProgramDetail) to
        // link with the assessment_id in a query param when available. Fallback: 404.
        const params = new URLSearchParams(window.location.search);
        const explicitId = params.get("assessment_id");
        if (!explicitId) {
          if (!cancelled) {
            setState({ status: "not_found", program, assessment: null, error: null });
          }
          return;
        }
        const metaRes = await api.get(`/academy/assessments/${explicitId}`);
        const metadata = metaRes.data;
        // Sanity check slug matches (defense-in-depth).
        if (metadata.slug && assessmentSlug && metadata.slug !== assessmentSlug) {
          if (!cancelled) setState({ status: "not_found", program, assessment: null, error: null });
          return;
        }
        if (!cancelled) {
          setState({ status: "ready", program, assessment: metadata, error: null });
          if (metadata.current_attempt) {
            // Rehydrate the in-progress attempt (fetch questions).
            const cur = await api.get(`/academy/attempts/${metadata.current_attempt.id}`);
            setAttempt(cur.data.attempt);
            setQuestions(cur.data.questions || []);
          }
        }
      } catch (err) {
        const detail = err?.response?.data?.detail;
        const code = typeof detail === "object" ? detail?.code : null;
        if (!cancelled) {
          if (err?.response?.status === 404 && code === "feature_disabled") {
            setState({ status: "disabled", program: null, assessment: null, error: null });
            return;
          }
          if (err?.response?.status === 403 && code === "not_enrolled") {
            setState({ status: "not_enrolled", program: null, assessment: null, error: null });
            return;
          }
          if (err?.response?.status === 404) {
            setState({ status: "not_found", program: null, assessment: null, error: null });
            return;
          }
          setState({ status: "error", program: null, assessment: null, error: formatApiError(err) });
        }
      }
    }
    if (!loadedOnce.current) {
      loadedOnce.current = true;
      load();
    }
  }, [programSlug, assessmentSlug]);

  const startAttempt = async () => {
    if (!state.assessment) return;
    setStarting(true);
    try {
      const res = await api.post(`/academy/assessments/${state.assessment.id}/attempts`);
      setAttempt(res.data.attempt);
      setQuestions(res.data.questions || []);
      setAnswers({});
      setSubmitResult(null);
    } catch (err) {
      toast.error(formatApiError(err) || "Could not start attempt.");
    } finally {
      setStarting(false);
    }
  };

  const onAnswerChange = (questionId, kind, newSelection) => {
    setAnswers((prev) => ({ ...prev, [questionId]: newSelection }));
  };

  const onSubmit = async () => {
    if (!attempt) return;
    setSubmitting(true);
    try {
      const payload = {
        answers: questions.map((q) => ({
          question_id: q.id,
          selected_option_ids: answers[q.id] || [],
        })),
      };
      const res = await api.post(`/academy/attempts/${attempt.id}/submit`, payload);
      setSubmitResult(res.data);
      setAttempt(res.data.attempt);
    } catch (err) {
      toast.error(formatApiError(err) || "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  const totalAnswered = useMemo(
    () => Object.values(answers).filter((v) => v && v.length > 0).length,
    [answers],
  );

  const backHref = lessonSlug
    ? `/academy/programs/${programSlug}/lessons/${lessonSlug}`
    : `/academy/programs/${programSlug}`;

  if (state.status === "loading") {
    return (
      <Shell>
        <div data-testid="academy-assessment-loading" className="flex items-center gap-2 text-v2-text-muted">
          <Loader2 className="w-4 h-4 animate-spin" />
          Loading assessment…
        </div>
      </Shell>
    );
  }
  if (state.status === "disabled") {
    return <Shell><Empty testId="academy-assessment-disabled" title="Academy is not yet available." back={{ to: "/dashboard", label: "Back to dashboard" }} /></Shell>;
  }
  if (state.status === "not_enrolled") {
    return <Shell><Empty testId="academy-assessment-not-enrolled" title="Enroll to take this assessment." hint="You need an active enrollment in the program." back={{ to: backHref, label: "Back" }} /></Shell>;
  }
  if (state.status === "not_found") {
    return <Shell><Empty testId="academy-assessment-not-found" title="Assessment not found." hint="It may have been unpublished or the link is missing an assessment id." back={{ to: backHref, label: "Back" }} /></Shell>;
  }
  if (state.status === "error") {
    return <Shell><div data-testid="academy-assessment-error" className="text-v2-text">{state.error}</div></Shell>;
  }

  const a = state.assessment;
  const hasResult = Boolean(submitResult);
  const inProgress = Boolean(attempt) && !hasResult;
  const readyToStart = !inProgress && !hasResult;

  return (
    <Shell testId="academy-assessment-take">
      <div className="mb-6 flex items-center gap-3 text-sm text-v2-text-muted">
        <Link
          to={backHref}
          data-testid="academy-assessment-back"
          className="inline-flex items-center gap-1.5 hover:text-v2-text"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          Back
        </Link>
      </div>

      <header className="mb-8 max-w-3xl">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-v2-text-muted">
          <ClipboardList className="w-3.5 h-3.5" />
          <span data-testid="academy-assessment-kind">{a.kind}</span>
          <span>·</span>
          <span>Scope: {a.scope}</span>
        </div>
        <h1
          data-testid="academy-assessment-title"
          className="mt-2 text-[28px] md:text-[36px] font-semibold tracking-tight text-v2-text"
          style={{ fontFamily: "'Space Grotesk', 'Inter', sans-serif" }}
        >
          {a.title}
        </h1>
        {a.description ? (
          <p data-testid="academy-assessment-description" className="mt-3 text-v2-text-muted whitespace-pre-wrap">
            {a.description}
          </p>
        ) : null}
        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
          <MetaChip icon={ClipboardList} label={`${a.question_count} question${a.question_count === 1 ? "" : "s"}`} />
          <MetaChip icon={ShieldCheck} label={`Pass ≥ ${a.pass_pct}%`} />
          <MetaChip icon={RefreshCcw} label={a.attempts_allowed === 0 ? "Unlimited attempts" : `${a.attempts_remaining ?? a.attempts_allowed} attempt${a.attempts_allowed === 1 ? "" : "s"} left`} />
          {a.time_limit_seconds ? <MetaChip icon={Clock} label={`${Math.round(a.time_limit_seconds/60)} min limit`} /> : null}
        </div>
      </header>

      {readyToStart ? (
        <div className="rounded-xl border border-v2-border bg-v2-surface p-8 max-w-3xl">
          {a.last_result ? (
            <div className="mb-6 rounded-lg border border-v2-border bg-v2-background p-4" data-testid="academy-assessment-last-result">
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-v2-text-muted">
                Previous result
              </div>
              <div className="mt-1 flex items-center gap-2">
                {a.last_result.passed ? (
                  <CheckCircle2 className="w-4 h-4 text-[var(--v2-tier-ready)]" />
                ) : (
                  <XCircle className="w-4 h-4 text-[var(--v2-danger)]" />
                )}
                <span className="text-v2-text font-semibold">
                  {Math.round(a.last_result.score_pct || 0)}% — {a.last_result.passed ? "Passed" : "Not passed"}
                </span>
              </div>
            </div>
          ) : null}
          <p className="text-v2-text-muted text-sm">
            Ready to begin? Submissions are graded immediately.
            {a.attempts_remaining !== null && a.attempts_remaining !== undefined && a.attempts_allowed > 0 ? (
              <span> You have <strong>{a.attempts_remaining}</strong> attempt{a.attempts_remaining === 1 ? "" : "s"} remaining.</span>
            ) : null}
          </p>
          <button
            type="button"
            onClick={startAttempt}
            disabled={starting || (a.attempts_allowed > 0 && a.attempts_remaining === 0)}
            data-testid="academy-assessment-start-btn"
            className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-[var(--v2-accent)] text-white font-semibold hover:opacity-90 disabled:opacity-50"
          >
            {starting ? "Starting…" : (a.attempts_allowed > 0 && a.attempts_remaining === 0) ? "No attempts left" : "Start attempt"}
          </button>
        </div>
      ) : null}

      {inProgress ? (
        <form
          onSubmit={(e) => { e.preventDefault(); onSubmit(); }}
          data-testid="academy-assessment-form"
          className="space-y-6 max-w-3xl"
        >
          {questions.map((q, idx) => (
            <AssessmentQuestion
              key={q.id}
              index={idx}
              question={q}
              selection={answers[q.id] || []}
              onChange={(newSel) => onAnswerChange(q.id, q.kind, newSel)}
            />
          ))}
          <div className="rounded-xl border border-v2-border bg-v2-surface p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <p data-testid="academy-assessment-answered-count" className="text-sm text-v2-text-muted">
              Answered {totalAnswered} of {questions.length}
            </p>
            <button
              type="submit"
              disabled={submitting}
              data-testid="academy-assessment-submit-btn"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-[var(--v2-accent)] text-white font-semibold hover:opacity-90 disabled:opacity-50"
            >
              {submitting ? "Submitting…" : "Submit"}
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </form>
      ) : null}

      {hasResult ? (
        <div className="max-w-3xl">
          <AssessmentResultPanel result={submitResult} onRetake={startAttempt} backHref={backHref} />
        </div>
      ) : null}
    </Shell>
  );
}

function Shell({ children, testId = "academy-assessment-shell" }) {
  return (
    <div data-testid={testId} className="min-h-screen bg-v2-background text-v2-text">
      <AppNav />
      <main className="max-w-6xl mx-auto px-4 md:px-8 pt-12 pb-24">{children}</main>
    </div>
  );
}

function MetaChip({ icon: Icon, label }) {
  return (
    <div className="rounded-lg border border-v2-border bg-v2-surface p-3 flex items-center gap-2 text-sm text-v2-text">
      <Icon className="w-3.5 h-3.5 text-v2-text-muted" />
      <span>{label}</span>
    </div>
  );
}

function Empty({ testId, title, hint, back }) {
  return (
    <div data-testid={testId} className="rounded-xl border border-v2-border bg-v2-surface p-10 text-center">
      <p className="text-v2-text font-semibold">{title}</p>
      {hint ? <p className="mt-2 text-sm text-v2-text-muted">{hint}</p> : null}
      {back ? (
        <Link
          to={back.to}
          className="mt-6 inline-flex items-center gap-2 text-sm text-v2-accent hover:underline"
        >
          <ArrowLeft className="w-4 h-4" />
          {back.label}
        </Link>
      ) : null}
    </div>
  );
}
